import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PWAUpdateDialog } from './PWAUpdateDialog';

interface UsePWAUpdateReturn {
  UpdateDialog: () => React.ReactElement;
}

export const usePWAUpdate = (): UsePWAUpdateReturn => {
  const { t } = useTranslation();
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  const handleUpdate = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      setShowUpdateDialog(false);
      // The page will be reloaded automatically when the new service worker takes over
    }
  }, [waitingWorker]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const handleControllerChange = () => {
      window.location.reload();
    };

    const handleServiceWorkerUpdate = (event: Event) => {
      const e = event as CustomEvent<{ sw: ServiceWorker }>;
      if (e.detail?.sw) {
        setWaitingWorker(e.detail.sw);
        setShowUpdateDialog(true);
      }
    };

    // Listen for controller change (when a new service worker takes over)
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Listen for custom 'swUpdated' event
    window.addEventListener('swUpdated', handleServiceWorkerUpdate as EventListener);

    // Check for updates periodically
    const updateInterval = setInterval(() => {
      navigator.serviceWorker.ready
        .then((registration) => registration.update())
        .catch((error) => console.error('Error updating service worker:', error));
    }, 60 * 60 * 1000); // Check every hour

    // Initial check for updates
    navigator.serviceWorker.ready
      .then((registration) => registration.update())
      .catch((error) => console.error('Error updating service worker:', error));

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      window.removeEventListener('swUpdated', handleServiceWorkerUpdate as EventListener);
      clearInterval(updateInterval);
    };
  }, []);

  const UpdateDialogComponent = useCallback(
    () => (
      <PWAUpdateDialog
        open={showUpdateDialog}
        onClose={() => setShowUpdateDialog(false)}
        onUpdate={handleUpdate}
      />
    ),
    [showUpdateDialog, handleUpdate]
  );

  return { UpdateDialog: UpdateDialogComponent };
};
